import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import NavbarAr from "@/components/NavbarAr";
import Footer from "@/components/Footer";
import FooterAr from "@/components/FooterAr";
import Brand from "@/components/Brand";

// CREWZ APP — premium coming-soon teaser for the CrewZNatioN mobile
// application. This is NOT product documentation: no implementation
// detail, no fabricated screenshots, no invented App Store / Play
// Store badges, and no fabricated launch date.
//
// COUNTDOWN CONTRACT:
//   The countdown component is intentionally DORMANT. When the owner
//   supplies a real UTC launch date, set LAUNCH_ISO to that ISO string
//   and the countdown will activate. Until then, `LAUNCH_ISO = null`
//   renders a tasteful "COMING SOON" state instead of counting down
//   to a fabricated date. Do not invent a value here.
//
// SCREENSHOT SHOWCASE:
//   Three device placeholders (aspect-[9/19.5], vertical) act as an
//   asset-ready region. When approved app screenshots are supplied,
//   drop them into SCREENSHOT_ASSETS (per index) and they will render
//   in-place. Until then a neutral wordmark placeholder is shown.
//
// STORE BADGES:
//   Kept OUT until the app actually ships. No fabricated App Store /
//   Google Play links.

// Set to a valid ISO 8601 UTC string (e.g. "2026-11-01T09:00:00Z")
// when the owner approves the launch moment. Until then it is null
// and the visible state is "COMING SOON".
const LAUNCH_ISO = null;

// Fill each slot when the owner supplies verified in-app screenshots
// hosted in `/app/frontend/public/assets/app/` (or an approved CDN).
// Each entry is `{ src, alt }`. When empty, the neutral wordmark
// placeholder renders in that slot.
const SCREENSHOT_ASSETS = [null, null, null];

const COPY = {
    en: {
        overline: "CrewZ App",
        title: (
            <>
                Your Garage. Your Community.
                <br />
                Your World.
            </>
        ),
        subtitle: (
            <>
                Built for enthusiasts. Built by <Brand />.
            </>
        ),
        lead:
            "A mobile home for the culture — the community you already know, in the palm of your hand. We are getting it ready.",
        comingSoon: "Coming Soon",
        countdownHeading: "Launches in",
        pillarsHeading: "What lives inside",
        pillars: [
            { key: "garage", title: "Garage", body: "Your automotive identity — the machines that make you smile." },
            { key: "community", title: "Community", body: "Profiles of the enthusiasts you already ride and gather with." },
            { key: "events", title: "Events", body: "Meets, drives and gatherings — how you find your way to them." },
            { key: "content", title: "Content", body: "Moments from the culture — as they happen, from the people living it." },
            { key: "marketplace", title: "Marketplace", body: "Enthusiast-to-enthusiast — parts, machines, and the stories behind them." },
        ],
        screenshotsHeading: "A first look",
        screenshotsPending: "Screenshots reveal on launch.",
        stayCloseHeading: "Stay close",
        stayCloseBody: (
            <>
                Follow <Brand /> across our channels for the announcement. We&rsquo;ll be first to tell you when doors open.
            </>
        ),
        followInstagram: "Follow on Instagram",
        followYoutube: "Watch on YouTube",
        metaTitle: "CrewZ App — Coming Soon | CrewZNatioN",
        metaDesc:
            "The CrewZNatioN mobile application — your garage, your community, and the events, content and marketplace of the culture, in the palm of your hand.",
        canonical: "https://www.crewznation.com/app",
        cdUnits: [
            { key: "d", label: "days" },
            { key: "h", label: "hours" },
            { key: "m", label: "minutes" },
            { key: "s", label: "seconds" },
        ],
        placeholderAlt: "CrewZNatioN app screenshot placeholder",
    },
    ar: {
        overline: (
            <>
                تطبيق <span dir="ltr">CREWZ</span>
            </>
        ),
        title: (
            <>
                قراجك. مجتمعك.
                <br />
                عالمك.
            </>
        ),
        subtitle: (
            <>
                مجتمع <Brand /> بين يديك
            </>
        ),
        lead: (
            <>
                كل ما يربطك بعالم <Brand />، في مكانٍ واحد
            </>
        ),
        comingSoon: "قريبًا",
        countdownHeading: "يُطلَق خلال",
        pillarsHeading: "ما بداخل التطبيق",
        pillars: [
            { key: "garage", title: "القراج", body: "هويّتك في عالم الميكانيكا — سيارتك أو دراجتك، وكل ما تعنيه لك." },
            { key: "community", title: "المجتمع", body: "ملفّات الأعضاء الذين تُصادفهم في الطريق واللقاءات." },
            { key: "events", title: "الفعاليات", body: "الاجتماعات والرحلات والفعاليات — طريقك للوصول إليها." },
            { key: "content", title: "المحتوى", body: "لحظات الثقافة كما تحدث، من الذين يعيشونها." },
            { key: "marketplace", title: "السوق", body: "من عضوٍ إلى عضو — قطعٌ ومركبات وحكاياتها." },
        ],
        screenshotsHeading: "لمحة أولى",
        screenshotsPending: "تُكشف اللقطات عند الإطلاق.",
        stayCloseHeading: "ابقَ قريبًا",
        stayCloseBody: (
            <>
                تابع <Brand /> عبر قنواتنا الرسمية — نحن أول من يُخبرك حين تُفتح الأبواب.
            </>
        ),
        followInstagram: "تابع على انستقرام",
        followYoutube: "شاهد على يوتيوب",
        metaTitle: "تطبيق CREWZ — قريبًا | CrewZNatioN",
        metaDesc:
            "تطبيق CrewZNatioN للأجهزة الجوّالة — قراجك، ومجتمعك، وفعاليات الثقافة ومحتواها وسوقها، بين يديك.",
        canonical: "https://www.crewznation.com/ar/app",
        cdUnits: [
            { key: "d", label: "يوم" },
            { key: "h", label: "ساعة" },
            { key: "m", label: "دقيقة" },
            { key: "s", label: "ثانية" },
        ],
        placeholderAlt: "لقطة تطبيق CrewZNatioN — عنصر نائب",
    },
};

// Compute the remaining d/h/m/s toward LAUNCH_ISO. Returns null when
// no launch date is configured or the moment has already passed
// (allowing the surface to gracefully degrade to "COMING SOON").
function useCountdown(launchIso) {
    const [remaining, setRemaining] = useState(() => diffToTarget(launchIso));
    useEffect(() => {
        if (!launchIso) return undefined;
        const id = setInterval(() => setRemaining(diffToTarget(launchIso)), 1000);
        return () => clearInterval(id);
    }, [launchIso]);
    return remaining;
}

function diffToTarget(launchIso) {
    if (!launchIso) return null;
    const target = new Date(launchIso).getTime();
    if (Number.isNaN(target)) return null;
    const now = Date.now();
    const delta = target - now;
    if (delta <= 0) return null;
    const s = Math.floor(delta / 1000);
    return {
        d: Math.floor(s / 86400),
        h: Math.floor((s % 86400) / 3600),
        m: Math.floor((s % 3600) / 60),
        s: s % 60,
    };
}

export default function CrewzApp({ locale = "en" }) {
    const isAr = locale === "ar";
    const Nav = isAr ? NavbarAr : Navbar;
    const PageFooter = isAr ? FooterAr : Footer;
    const t = isAr ? COPY.ar : COPY.en;
    const countdown = useCountdown(LAUNCH_ISO);
    const active = !!(LAUNCH_ISO && countdown);

    useEffect(() => {
        const prevTitle = document.title;
        document.title = t.metaTitle;

        let m = document.querySelector('meta[name="description"]');
        const prevDesc = m ? m.getAttribute("content") : null;
        if (!m) {
            m = document.createElement("meta");
            m.setAttribute("name", "description");
            document.head.appendChild(m);
        }
        m.setAttribute("content", t.metaDesc);

        let c = document.querySelector('link[rel="canonical"]');
        const prevCanonical = c ? c.getAttribute("href") : null;
        if (!c) {
            c = document.createElement("link");
            c.setAttribute("rel", "canonical");
            document.head.appendChild(c);
        }
        c.setAttribute("href", t.canonical);

        return () => {
            document.title = prevTitle;
            if (m && prevDesc !== null) m.setAttribute("content", prevDesc);
            if (c) {
                if (prevCanonical !== null) c.setAttribute("href", prevCanonical);
                else c.parentNode?.removeChild(c);
            }
        };
    }, [t.metaTitle, t.metaDesc, t.canonical]);

    return (
        <div
            dir={isAr ? "rtl" : "ltr"}
            className={`min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)] ${isAr ? "font-arabic" : "font-body"}`}
            data-testid={isAr ? "crewz-app-page-ar" : "crewz-app-page"}
        >
            <Nav />

            {/* AMBIENT HERO — dark gradient with a subtle gold rule
                accent. No fabricated device mock at the top; the hero
                carries only the promise. */}
            <section
                className="relative overflow-hidden border-b border-white/5"
                data-testid="crewz-app-hero"
            >
                {/* Ambient background — a large soft gold radial glow at
                    one edge, plus a very fine grid grain via the site's
                    existing `.grain` texture from App.css. */}
                <div
                    aria-hidden="true"
                    className={`pointer-events-none absolute inset-0 opacity-70 ${isAr ? "" : ""}`}
                    style={{
                        background: isAr
                            ? "radial-gradient(60% 60% at 15% 30%, rgba(197,160,74,0.16) 0%, rgba(5,5,5,0) 60%)"
                            : "radial-gradient(60% 60% at 85% 30%, rgba(197,160,74,0.16) 0%, rgba(5,5,5,0) 60%)",
                    }}
                />
                <div className="relative px-6 md:px-12 lg:px-20 pt-24 md:pt-32 lg:pt-40 pb-16 md:pb-24 max-w-6xl mx-auto">
                    <div className={`flex items-center gap-3 mb-5 ${isAr ? "justify-end" : ""}`}>
                        <span className="gold-rule" />
                        <p
                            className={`overline overline-primary ${isAr ? "font-arabic !text-[22.04px] md:!text-[23.8px]" : ""}`}
                            data-testid="crewz-app-overline"
                        >
                            {t.overline}
                        </p>
                    </div>
                    <h1
                        className={`${isAr ? "font-arabic text-4xl md:text-5xl lg:text-6xl !leading-[1.25]" : "font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.015em]"} text-[var(--crewz-text)] max-w-4xl`}
                        data-testid="crewz-app-title"
                    >
                        {t.title}
                    </h1>
                    <p
                        className={`${isAr ? "font-arabic text-lg md:text-xl leading-[1.95]" : "font-body text-lg md:text-xl leading-relaxed font-light"} text-[var(--crewz-text)]/85 mt-8 max-w-2xl`}
                        data-testid="crewz-app-subtitle"
                    >
                        {t.subtitle}
                    </p>
                    <p
                        className={`${isAr ? "font-arabic text-lg md:text-xl leading-[1.95]" : "font-body text-base md:text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/70 mt-4 max-w-2xl`}
                        data-testid="crewz-app-lead"
                    >
                        {t.lead}
                    </p>

                    {/* COUNTDOWN — DORMANT until LAUNCH_ISO is set.
                        Active: 4-cell d/h/m/s row.
                        Dormant: single tasteful "COMING SOON" pill.
                        Both variants share the same slot so the layout
                        does not shift when the owner activates it. */}
                    <div className="mt-12" data-testid="crewz-app-countdown-slot">
                        {active ? (
                            <>
                                <p
                                    className={`overline text-zinc-500 mb-4 ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px]" : ""}`}
                                >
                                    {t.countdownHeading}
                                </p>
                                <div
                                    className="grid grid-cols-4 gap-3 md:gap-4 max-w-2xl"
                                    dir="ltr"
                                    data-testid="crewz-app-countdown-active"
                                >
                                    {t.cdUnits.map((u) => (
                                        <div
                                            key={u.key}
                                            className="bg-black/40 border border-white/10 px-3 py-4 md:px-5 md:py-6 text-center"
                                        >
                                            <p className="font-display text-3xl md:text-5xl text-[var(--crewz-gold)] leading-none tabular-nums">
                                                {String(countdown[u.key]).padStart(2, "0")}
                                            </p>
                                            <p
                                                className={`mt-2 text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-zinc-400 ${isAr ? "font-arabic normal-case tracking-normal !text-[13px]" : ""}`}
                                            >
                                                {u.label}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div
                                className={`inline-flex items-center gap-3 border border-[var(--crewz-gold)]/60 px-6 py-3 ${isAr ? "flex-row-reverse" : ""}`}
                                data-testid="crewz-app-countdown-dormant"
                            >
                                <span className="w-2 h-2 rounded-full bg-[var(--crewz-gold)] animate-pulse" aria-hidden="true" />
                                <span
                                    className={`${isAr ? "font-arabic text-base font-bold" : "font-body text-[13px] tracking-[0.28em] uppercase font-bold"} text-[var(--crewz-gold)]`}
                                >
                                    {t.comingSoon}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* PILLARS — high-level product pillars (Garage, Community,
                Events, Content, Marketplace). Concise and aspirational,
                no feature promises, no timelines. */}
            <section
                className="px-6 md:px-12 lg:px-20 py-16 md:py-24 lg:py-32 max-w-6xl mx-auto"
                data-testid="crewz-app-pillars"
            >
                <div className={`flex items-center gap-3 mb-8 ${isAr ? "justify-end" : ""}`}>
                    <span className="gold-rule" />
                    <p
                        className={`overline ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px]" : ""}`}
                    >
                        {t.pillarsHeading}
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-6 lg:gap-4">
                    {t.pillars.map((p, i) => (
                        <div
                            key={p.key}
                            className={`${isAr ? "text-right" : ""}`}
                            data-testid={`crewz-app-pillar-${p.key}`}
                        >
                            <p
                                className={`${isAr ? "font-arabic !text-[17px]" : "font-body text-[14px] tracking-[0.28em]"} uppercase font-bold text-[var(--crewz-gold)] mb-3`}
                            >
                                {String(i + 1).padStart(2, "0")}
                            </p>
                            <p
                                className={`${isAr ? "font-arabic text-2xl !leading-[1.3]" : "font-display text-2xl leading-[1.1]"} text-[var(--crewz-text)]`}
                            >
                                {p.title}
                            </p>
                            <p
                                className={`${isAr ? "font-arabic text-base leading-[1.9]" : "font-body text-base leading-relaxed font-light"} text-[var(--crewz-text)]/70 mt-3`}
                            >
                                {p.body}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* SCREENSHOT SHOWCASE — three vertical device slots. Each
                slot accepts a real screenshot when SCREENSHOT_ASSETS[i]
                is set (owner-approved asset). Until then a neutral
                wordmark placeholder holds the geometry so no invented
                UI is shown. */}
            <section
                className="px-6 md:px-12 lg:px-20 pb-24 md:pb-32 max-w-6xl mx-auto"
                data-testid="crewz-app-screenshots"
            >
                <div className={`flex items-center gap-3 mb-10 ${isAr ? "justify-end" : ""}`}>
                    <span className="gold-rule" />
                    <p
                        className={`overline ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px]" : ""}`}
                    >
                        {t.screenshotsHeading}
                    </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-10 max-w-4xl mx-auto">
                    {SCREENSHOT_ASSETS.map((asset, i) => (
                        <div
                            key={i}
                            className="relative aspect-[9/19.5] rounded-[36px] border border-white/10 bg-gradient-to-b from-white/[0.03] to-white/[0.01] overflow-hidden"
                            data-testid={`crewz-app-screenshot-slot-${i}`}
                        >
                            {asset ? (
                                <img
                                    src={asset.src}
                                    alt={asset.alt || t.placeholderAlt}
                                    className="absolute inset-0 h-full w-full object-cover"
                                    loading="lazy"
                                />
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="text-center px-4">
                                        <p
                                            className={`${isAr ? "font-arabic text-lg" : "font-display text-lg"} text-[var(--crewz-gold)]/80`}
                                        >
                                            <Brand />
                                        </p>
                                        <p
                                            className={`${isAr ? "font-arabic normal-case tracking-normal !text-[15px]" : "font-body text-[13px] tracking-[0.22em]"} uppercase text-zinc-500 mt-1`}
                                        >
                                            {t.comingSoon}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                {SCREENSHOT_ASSETS.every((s) => !s) && (
                    <p
                        className={`${isAr ? "font-arabic text-sm text-center" : "font-body text-xs tracking-[0.22em] uppercase text-center"} text-zinc-500 mt-8`}
                    >
                        {t.screenshotsPending}
                    </p>
                )}
            </section>

            {/* STAY CLOSE — brand channels, no fabricated store badges. */}
            <section
                className="px-6 md:px-12 lg:px-20 pb-24 md:pb-32 max-w-4xl mx-auto"
                data-testid="crewz-app-stay-close"
            >
                <div className="border-t border-white/10 pt-14 md:pt-16">
                    <div className={`flex items-center gap-3 mb-5 ${isAr ? "justify-end" : ""}`}>
                        <span className="gold-rule" />
                        <p
                            className={`overline ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px]" : ""}`}
                        >
                            {t.stayCloseHeading}
                        </p>
                    </div>
                    <p
                        className={`${isAr ? "font-arabic text-lg md:text-xl leading-[1.95]" : "font-body text-xl leading-relaxed font-light"} text-[var(--crewz-text)]/85 max-w-2xl`}
                    >
                        {t.stayCloseBody}
                    </p>
                    <div className={`mt-8 flex flex-wrap gap-6 ${isAr ? "justify-end" : ""}`}>
                        <a
                            href="https://instagram.com/CrewZNatioN"
                            target="_blank"
                            rel="noreferrer noopener"
                            className="text-[var(--crewz-gold)] hover:opacity-80 transition-opacity underline-offset-4 hover:underline text-base"
                            data-testid="crewz-app-instagram-link"
                        >
                            {t.followInstagram}
                        </a>
                        <a
                            href="https://youtube.com/@CrewZNatioN"
                            target="_blank"
                            rel="noreferrer noopener"
                            className="text-[var(--crewz-gold)] hover:opacity-80 transition-opacity underline-offset-4 hover:underline text-base"
                            data-testid="crewz-app-youtube-link"
                        >
                            {t.followYoutube}
                        </a>
                    </div>
                </div>
            </section>

            <PageFooter />
        </div>
    );
}
